import { observer } from 'mobx-react-lite';
import { type ReactNode, useCallback, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, IconButton, Text, XStack, YStack } from '@/components/ui';
import { useConnectCalendar } from '@/integrations/useConnectCalendar';
import { withUIDelay } from '@/lib/animate';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import { electronApis } from '../electron-api';

import { meetingApis } from './meetings.query';
import { meetingStore } from './meetings.state';
import { type MeetingPermissionType } from './meetings.types';

const CALENDAR_SKIPPED_KEY = 'meeting-onboarding-calendar-skipped';

export const OnboardingChecklist = observer(function OnboardingChecklist() {
  const [calendarSkipped, setCalendarSkipped] = useState(
    () => getLocalStorageItem(CALENDAR_SKIPPED_KEY) ?? false
  );
  const onSkipCalendar = useCallback(() => {
    setCalendarSkipped(true);
    setLocalStorageItem(CALENDAR_SKIPPED_KEY, true);
  }, []);

  if (!meetingStore.shouldShowOnboardingChecklist) return null;

  const micGranted = meetingStore.hasMicrophonePermission;
  const calendarConnected = userStore.calendarConnected;
  const calendarDone = calendarConnected || calendarSkipped;
  const meetingStatus = meetingStore.recording.status;
  const isRecording =
    meetingStatus === 'recording' || meetingStatus === 'loading';

  const activeStep = !micGranted ? 1 : !calendarDone ? 2 : 3;

  const handleDismiss = () => {
    meetingStore.setOnboardingChecklistCompleted(true);
  };

  return (
    <Checklist.Root
      title="Get started with Meeting Notes"
      onDismiss={handleDismiss}
    >
      <MicPermissionItem active={activeStep === 1} />
      <Checklist.Connector />
      <CalendarItem
        calendarConnected={calendarConnected}
        calendarSkipped={calendarSkipped}
        onSkip={onSkipCalendar}
        active={activeStep === 2}
      />
      <Checklist.Connector />
      <TryItOutItem
        disabled={activeStep !== 3 || isRecording}
        isLoading={meetingStatus === 'loading'}
        active={activeStep === 3}
      />
    </Checklist.Root>
  );
});

const MicPermissionItem = observer(function MicPermissionItem({
  active,
}: {
  active: boolean;
}) {
  const hasMicrophonePermission = meetingStore.hasMicrophonePermission;
  const [showRestartButton, setShowRestartButton] = useState(false);

  if (hasMicrophonePermission) {
    return (
      <Checklist.Item completed active={false} stepNumber={1}>
        <Checklist.Content
          title="Microphone access granted"
          description="Your mic is ready for meeting recordings"
        />
      </Checklist.Item>
    );
  }

  const requestPermission = async (permission: MeetingPermissionType) => {
    try {
      const updatedPermissions = await meetingApis.requestMacPermissions([
        permission,
      ]);
      if (!updatedPermissions.mic) {
        withUIDelay(() => setShowRestartButton(true));
      }
    } catch (error) {
      console.error(`Failed to request ${permission} permission:`, error);
    }
  };

  const handleClick = () => {
    if (showRestartButton) {
      electronApis.system.relaunchApp();
      return;
    }
    requestPermission('microphone');
  };

  const title = showRestartButton
    ? 'Relaunch to apply'
    : 'Grant microphone access';
  const description = showRestartButton
    ? 'The app needs to restart to enable recording'
    : 'Lets Littlebird capture conversations and generate summaries';
  const buttonText = showRestartButton ? 'Relaunch' : 'Grant Permission';

  return (
    <Checklist.Item completed={false} active={active} stepNumber={1}>
      <Checklist.Content title={title} description={description} />
      <Checklist.Action>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleClick}
          className="min-w-28"
        >
          {buttonText}
        </Button>
      </Checklist.Action>
    </Checklist.Item>
  );
});

function CalendarItem({
  calendarConnected,
  calendarSkipped,
  onSkip,
  active,
}: {
  calendarConnected: boolean;
  calendarSkipped: boolean;
  onSkip: () => void;
  active: boolean;
}) {
  const { isConnecting, connect } = useConnectCalendar();

  const completed = calendarConnected || calendarSkipped;

  return (
    <Checklist.Item completed={completed} active={active} stepNumber={2}>
      <Checklist.Content
        title={
          calendarConnected
            ? 'Calendar connected'
            : calendarSkipped
              ? 'Calendar skipped'
              : 'Connect your calendar'
        }
        description={
          calendarConnected
            ? 'Meetings will be detected automatically'
            : calendarSkipped
              ? 'You can connect your calendar later in Settings'
              : 'Automatically detect meetings and start capturing notes'
        }
      />
      {!completed && (
        <Checklist.Action>
          <XStack className="gap-1 items-center">
            <Button
              size="sm"
              variant="ghost"
              onClick={onSkip}
              disabled={!active}
            >
              Skip
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={connect}
              isLoading={isConnecting}
              disabled={!active}
              className="min-w-28"
            >
              Connect
            </Button>
          </XStack>
        </Checklist.Action>
      )}
    </Checklist.Item>
  );
}

function TryItOutItem({
  disabled,
  isLoading,
  active,
}: {
  disabled: boolean;
  isLoading: boolean;
  active: boolean;
}) {
  const handleClick = () => {
    if (meetingStore.isGuidedOnboardingRecording) return;
    meetingStore.setIsGuidedOnboardingRecording(true);
    meetingStore.meetingStartedFromOverview = true;
    meetingApis
      .enqueueStartMeeting({ payload: { startReason: 'manual' } })
      .catch(() => {
        meetingStore.setIsGuidedOnboardingRecording(false);
        meetingStore.meetingStartedFromOverview = false;
      });
  };

  return (
    <Checklist.Item completed={false} active={active} stepNumber={3}>
      <Checklist.Content
        title="Record your first note"
        description="Try it out — talk for 30 seconds and see an AI summary"
      />
      <Checklist.Action>
        <Button
          size="sm"
          onClick={handleClick}
          disabled={disabled}
          isLoading={isLoading}
          className="min-w-28"
        >
          Try it out
        </Button>
      </Checklist.Action>
    </Checklist.Item>
  );
}

// --- Compound component ---

function ChecklistRoot({
  title,
  description,
  onDismiss,
  children,
}: {
  title: string;
  description?: string;
  onDismiss: () => void;
  children: ReactNode;
}) {
  return (
    <YStack
      data-testid="meetings-onboarding-checklist"
      className="relative rounded-2xl border border-border-strong bg-surface px-8 pt-5 pb-8"
    >
      <IconButton
        size="sm"
        variant="ghost"
        icon={Icons.cancel}
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3"
      />
      <YStack className="gap-0.5 mb-10">
        <Text className="font-serif text-base text-foreground">{title}</Text>
        {description && (
          <Text className="text-sm text-foreground-muted">{description}</Text>
        )}
      </YStack>
      {children}
    </YStack>
  );
}

function ChecklistItem({
  completed,
  active,
  stepNumber,
  children,
}: {
  completed: boolean;
  active: boolean;
  stepNumber: number;
  children: ReactNode;
}) {
  return (
    <XStack className="items-center gap-4">
      <div className="shrink-0">
        {completed ? (
          <div className="size-8 rounded-full bg-brand-emerald-green flex items-center justify-center">
            <Icons.check className="size-4 text-white" />
          </div>
        ) : (
          <div
            className={cn(
              'size-8 rounded-full border-2 flex items-center justify-center',
              active ? 'border-border-strongest' : 'border-border-strong'
            )}
          >
            <span
              className={cn(
                'text-sm',
                active ? 'text-foreground' : 'text-foreground-muted'
              )}
            >
              {stepNumber}
            </span>
          </div>
        )}
      </div>
      {children}
    </XStack>
  );
}

function ChecklistConnector() {
  return <div className="h-8 my-1.5 w-px bg-border-strongest ml-4" />;
}

function ChecklistContent({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <YStack className="min-w-0 flex-1 gap-0.5">
      <Text className="text-sm font-medium text-foreground">{title}</Text>
      <Text className="text-sm text-foreground-muted">{description}</Text>
    </YStack>
  );
}

function ChecklistAction({ children }: { children: ReactNode }) {
  return <div className="shrink-0 ml-auto">{children}</div>;
}

const Checklist = {
  Root: ChecklistRoot,
  Item: ChecklistItem,
  Connector: ChecklistConnector,
  Content: ChecklistContent,
  Action: ChecklistAction,
};
